import { useQuery } from "@apollo/client";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import DocumentationIconButton from "components/DocumentationIconButton";
import FeedbackButton from "components/FeedbackButton";
import PageLoading from "components/loading/PageLoading";
import DesktopView from "components/Mobile/DesktopView";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Tab from "ds/components/Tab";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";
import { AccountContext } from "views/AccountWrapper";

import ConfigManagement from "../ConfigManagement";
import IacManagementResourceDetails from "../IacManagement/ResourceDetails";
import { GET_ACCOUNT_STACKS } from "./gql";
import ResourcesIacManagement from "./IacManagement";

const ResourcesWrapper = () => {
  const { pathname } = useLocation();
  const { accountName } = useTypedContext(AccountContext);

  useTitle(`Resources · ${accountName}`);

  const { error, loading, data } = useQuery<{
    hasAnsibleStacks?: boolean;
  }>(GET_ACCOUNT_STACKS);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading) {
    return <PageLoading />;
  }

  const configurationManagementEnabled = !!data?.hasAnsibleStacks;

  return (
    <>
      <ViewHeader>
        <ViewHeaderTitle>Resources</ViewHeaderTitle>
        <DesktopView>
          <ViewHeaderWrapper direction="row" align="center">
            {configurationManagementEnabled &&
              pathname === "/resources/configuration-management" && (
                <FeedbackButton id="config-management-in-resources-feedback" />
              )}
            {pathname === "/resources/iac-management" && (
              <FeedbackButton id="iac-management-in-resources-feedback" />
            )}
            <DocumentationIconButton
              href={getDocsUrl("/concepts/resources#account-level-resources")}
              tooltipText="Go to account-level resources documentation"
            />
          </ViewHeaderWrapper>
        </DesktopView>

        {configurationManagementEnabled && (
          <ViewHeaderNavigation>
            <Tab id="iac" to="/resources/iac-management" label="IaC management" />

            <Tab
              id="config-mgmt"
              data-userflow-id="resources-configuration-management"
              to="/resources/configuration-management"
              label="Configuration management"
            />
          </ViewHeaderNavigation>
        )}
      </ViewHeader>

      <Routes>
        <Route path="/iac-management" element={<ResourcesIacManagement />}>
          <Route path="resource/:resourceId" element={<IacManagementResourceDetails />} />
        </Route>

        {configurationManagementEnabled && (
          <Route path="/configuration-management" element={<ConfigManagement />} />
        )}

        <Route path="*" element={<Navigate to="/resources/iac-management" replace />} />
      </Routes>
    </>
  );
};

export default ResourcesWrapper;
